
import React, { useEffect, useRef } from 'react';
import { Loader2, Navigation } from 'lucide-react';
import { LocationSuggestion } from '@/hooks/ride/useLocationInput';
import { cn } from '@/lib/utils';

interface LocationSuggestionsListProps {
  id?: string;
  inputValue: string;
  suggestions: LocationSuggestion[];
  recentLocations: string[];
  showCurrentLocation?: boolean;
  onCurrentLocation?: () => void;
  isGettingCurrentLocation?: boolean;
  isLoading: boolean;
  onSelectLocation: (location: string) => void;
}

const LocationSuggestionsList: React.FC<LocationSuggestionsListProps> = ({
  id,
  inputValue,
  suggestions,
  recentLocations,
  showCurrentLocation = false,
  onCurrentLocation,
  isGettingCurrentLocation = false,
  isLoading,
  onSelectLocation
}) => {
  // Generate unique IDs for accessibility
  const listId = id || 'location-suggestions';
  const hasContent = showCurrentLocation || recentLocations.length > 0 || suggestions.length > 0 || isLoading;
  
  // Ref for the first option to enable keyboard focus
  const firstOptionRef = useRef<HTMLDivElement>(null);
  
  // Handle keyboard navigation within the suggestions list
  const handleKeyNavigation = (e: React.KeyboardEvent<HTMLDivElement>, index: number, items: unknown[], onSelect: () => void) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        onSelect();
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const nextElement = e.currentTarget.parentElement?.querySelector(`[data-index="${index + 1}"]`) as HTMLElement;
        if (nextElement) nextElement.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevElement = e.currentTarget.parentElement?.querySelector(`[data-index="${index - 1}"]`) as HTMLElement;
        if (prevElement) prevElement.focus();
        else if (index === 0) {
          // If at the first item, move focus back to input
          // The input is associated via aria-controls
          const inputId = e.currentTarget.closest('[role="listbox"]')?.getAttribute('aria-labelledby');
          if (inputId) document.getElementById(inputId)?.focus();
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        // Close the popover and focus the input field
        const inputId = e.currentTarget.closest('[role="listbox"]')?.getAttribute('aria-labelledby');
        if (inputId) document.getElementById(inputId)?.focus();
        break;
      }
    }
  };

  // Announce results to screen readers
  const getAriaLiveMessage = () => {
    if (isLoading && inputValue) return "Searching for locations...";
    if (suggestions.length > 0) return `${suggestions.length} locations found`;
    if (inputValue && inputValue.length >= 2 && !isLoading) return "No locations found";
    return "";
  };
  
  return (
    <div
      id={listId}
      role="listbox"
      aria-label="Location suggestions"
      className={cn(
        // Anchored under the field and matching its width, so the list reads as
        // an extension of the input rather than a floating panel. Absolute
        // rather than portalled: see LocationInput for why this is not a
        // Radix Popover.
        'absolute left-0 right-0 top-full z-50 mt-1',
        'rounded-md border border-border bg-popover text-popover-foreground shadow-lg',
        'overflow-y-auto animate-in fade-in-0 zoom-in-95',
        // Bounded so a long list scrolls instead of running off the viewport,
        // which is what used to push it up over the site header.
        'max-h-[min(24rem,60vh)]',
      )}
    >
      <div 
        className="py-2"
        aria-live="polite" 
        aria-atomic="true"
        aria-relevant="additions text"
      >
        <div className="sr-only" role="status">{getAriaLiveMessage()}</div>
        
        {showCurrentLocation && onCurrentLocation && (
          <div
            ref={firstOptionRef}
            className="px-4 py-2.5 hover:bg-muted cursor-pointer flex items-center gap-2 transition-colors outline-none focus-visible:bg-accent focus-visible:text-accent-foreground focus:bg-muted"
            onClick={onCurrentLocation}
            onKeyDown={(e) => handleKeyNavigation(e, -1, [], onCurrentLocation)}
            role="option"
            tabIndex={0}
            aria-label="Use current location"
            data-index="-1"
          >
            <Navigation className="h-4 w-4 text-blue-500" aria-hidden="true" />
            <span className="font-medium">Current Location</span>
            {isGettingCurrentLocation && (
              <Loader2 className="h-4 w-4 animate-spin ml-auto" aria-hidden="true" />
            )}
          </div>
        )}
        
        {recentLocations.length > 0 && (
          <>
            <div 
              className="px-4 py-1 text-xs font-medium text-muted-foreground"
              id={`${listId}-recent-heading`}
              role="presentation"
            >
              Recent
            </div>
            <div role="group" aria-labelledby={`${listId}-recent-heading`}>
              {recentLocations.map((location, index) => (
                <div 
                  key={`recent-${index}-${location}`}
                  className="px-4 py-2.5 hover:bg-muted cursor-pointer flex items-center gap-2 transition-colors outline-none focus-visible:bg-accent focus-visible:text-accent-foreground focus:bg-muted"
                  onClick={() => onSelectLocation(location)}
                  onKeyDown={(e) => handleKeyNavigation(e, index, recentLocations, () => onSelectLocation(location))}
                  role="option"
                  tabIndex={index === 0 && !showCurrentLocation ? 0 : -1}
                  aria-label={`Recent location: ${location}`}
                  data-index={index}
                  ref={index === 0 && !showCurrentLocation ? firstOptionRef : null}
                 >
                   <div className="h-4 w-4 rounded-full bg-orange-500 flex-shrink-0" aria-hidden="true" />
                   <span>{location}</span>
                 </div>
              ))}
            </div>
            {(suggestions.length > 0 || isLoading) && (
              <div className="border-t border-muted my-1" role="presentation" aria-hidden="true"></div>
            )}
          </>
        )}
        
        {(suggestions.length > 0 || isLoading && inputValue) && (
          <div 
            className="px-4 py-1 text-xs font-medium text-muted-foreground"
            id={`${listId}-suggestions-heading`}
            role="presentation"
          >
            {inputValue ? "Suggestions" : "Nearby Places"}
          </div>
        )}
        
        {isLoading && inputValue && (
          <div 
            className="px-4 py-2.5 flex items-center gap-2"
            aria-live="polite"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="text-sm text-muted-foreground">Searching addresses...</span>
          </div>
        )}
        
        {!isLoading && !suggestions.length && inputValue && inputValue.length >= 2 && (
          <div 
            className="px-4 py-2.5 text-sm text-muted-foreground"
            aria-live="polite"
          >
            No address suggestions found
          </div>
        )}
        
        {suggestions.length > 0 && (
          <div 
            className="space-y-0.5" 
            role="group" 
            aria-labelledby={`${listId}-suggestions-heading`}
          >
            {suggestions.map((suggestion, index) => {
              const startIndex = recentLocations.length;
              const totalIndex = startIndex + index;
              
              return (
                <div 
                  key={`suggestion-${index}-${suggestion.text}`}
                  className="px-4 py-2.5 hover:bg-muted cursor-pointer flex items-center gap-2 transition-colors outline-none focus-visible:bg-accent focus-visible:text-accent-foreground focus:bg-muted"
                  onClick={() => onSelectLocation(suggestion.place_name)}
                  onKeyDown={(e) => handleKeyNavigation(e, totalIndex, suggestions, () => onSelectLocation(suggestion.place_name))}
                  role="option"
                  tabIndex={totalIndex === 0 && !showCurrentLocation && !recentLocations.length ? 0 : -1}
                  aria-label={`${suggestion.text}, ${suggestion.place_name}`}
                  data-index={totalIndex}
                  ref={totalIndex === 0 && !showCurrentLocation && !recentLocations.length ? firstOptionRef : null}
                 >
                   <div className="flex-shrink-0" aria-hidden="true">
                     <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                       <div className="h-2 w-2 rounded-full bg-primary-foreground" />
                     </div>
                   </div>
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium truncate">{suggestion.text}</span>
                    <span className="text-xs text-muted-foreground truncate">{suggestion.place_name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!hasContent && (
          <div 
            className="px-4 py-3 text-sm text-center text-muted-foreground"
            aria-live="polite"
          >
            Start typing to search for locations
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSuggestionsList;
